import React from 'react';
import { motion } from 'framer-motion';
import { BiDna } from 'react-icons/bi';
import { DNA_TRAITS, MAX_TRAIT_DISPLAY, DNA_LEVELS } from '../config/dnaSystem.js';

const StreamerDNAWidget = ({ dna, className = '' }) => {
  if (!dna || !dna.traits) return null;

  // Sort traits by score and get top traits
  const sortedTraits = Object.entries(dna.traits)
    .sort(([, a], [, b]) => b.score - a.score)
    .slice(0, MAX_TRAIT_DISPLAY);

  // Find the highest score to calculate percentages
  const maxScore = Math.max(...sortedTraits.map(([, trait]) => trait.score));

  // Calculate DNA level
  const totalScore = Object.values(dna.traits).reduce((sum, trait) => sum + trait.score, 0);
  const currentLevel = Object.entries(DNA_LEVELS)
    .reverse()
    .find(([, level]) => totalScore >= level.minScore);
  
  return (
    <div className={`bg-harmony-blue-dark rounded-xl p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-harmony-white flex items-center gap-2">
          <BiDna className="text-2xl text-harmony-orange" />
          Streamer DNA
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-harmony-white/60 text-sm">Level {currentLevel?.[0] || 1}</span>
          <span className="px-2 py-1 bg-harmony-orange/20 text-harmony-orange text-xs rounded-full">
            {currentLevel?.[1].label || DNA_LEVELS[1].label}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {sortedTraits.map(([traitId, trait], index) => {
          const traitConfig = DNA_TRAITS[traitId];
          if (!traitConfig) return null;

          const percentage = (trait.score / maxScore) * 100;

          return (
            <div key={traitId} className="relative">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{traitConfig.icon}</span>
                  <span className="text-harmony-white font-medium">{traitConfig.label}</span>
                </div>
                <span className="text-harmony-white/60 text-sm">{trait.score}</span>
              </div>
              <div className="h-2 bg-harmony-blue/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="h-full bg-harmony-orange rounded-full"
                />
              </div>
              <div className="mt-1">
                <p className="text-harmony-white/40 text-xs">{traitConfig.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-harmony-white/10">
        <div className="flex justify-between items-center text-sm">
          <span className="text-harmony-white/60">Total Score</span>
          <span className="text-harmony-white font-medium">{totalScore}</span>
        </div>
        <div className="mt-2 text-harmony-white/40 text-xs text-center">
          {Object.values(dna.traits).length === 0 ? (
            'No traits voted yet'
          ) : (
            'Voted by viewers during streams'
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamerDNAWidget; 